import { Button } from '#app/components/Buttons/Button.js';
import { CircularIconButton } from '#app/components/Buttons/CircularIconButton.js';
import { Link } from '#app/components/Link/index.js';
import { Text } from '#app/components/Primitives/Text.js';
import { useAddUserInterfaceFlag } from '#app/hooks/useAddUserInterfaceFlag.js';
import { useAnalytics } from '#app/hooks/useAnalytics.js';
import { useCurrentUser } from '#app/hooks/useCurrentUser.js';
import { useDeviceCategory } from '#app/hooks/useDeviceCategory.js';
import { useHasUserInterfaceFlags } from '#app/hooks/useHasUserInterfaceFlags.js';
import { useUpgradeToPro } from '#app/hooks/useUpgradeToPro.js';
import templates from './assets/portfolio-templates-preview@2x.webp';
import shape from './assets/quarter-torus-shape.webp';
import { CloseIcon } from '@contra/icons/icons';
import { getRoute } from '@contra/routes';
import { captureException } from '@contra/sentry/react';
import { useCallback } from 'react';
import { css, styled } from 'styled-components';

const Container = styled.div`
  width: 100%;
  position: relative;
  overflow: hidden;
  border-radius: 10px;
  background-color: ${({ theme }) => theme.colorsV2.black};
  background-clip: padding-box;

  margin-bottom: 16px;
`;

const Column = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  width: 100%;
  color: ${({ theme }) => theme.colorsV2.white};
  padding: 16px;

  margin-top: 192px;

  & > a {
    width: 100%;
  }

  ${({ theme }) => theme.mediaQueries.md} {
    padding: 24px;
    margin-top: 0;

    & > a {
      width: initial;
    }
  }
`;

const Templates = styled.img`
  ${({ theme }) => css`
    position: absolute;
    z-index: 2;
    width: 312px;
    left: 50%;
    margin-left: -156px;
    top: 24px;
    ${theme.mediaQueries.md} {
      margin-left: 0;

      width: 332px;
      top: 50%;
      margin-top: -88px;
      left: 452px;
    }
  `}
`;

const Shape = styled.img`
  ${({ theme }) => css`
    position: absolute;
    width: 480px;
    top: -180px;
    left: -132px;
    transform: rotate(95deg);

    ${theme.mediaQueries.md} {
      width: 656px;

      top: -216px;
      left: 336px;
      transform: rotate(150deg);
    }
  `}
`;

const CloseButton = styled(CircularIconButton)`
  ${({ theme }) => css`
    position: absolute;
    color: ${theme.colorsV2.white};
    background-color: ${theme.colorsV2.black};
    border: none;
    z-index: 3;
    top: 0;
    right: 0;
  `}
`;

export const ExplorePortfoliosCallout = () => {
  const isHidden = useHasUserInterfaceFlags(
    'HIDE_EXPLORE_CONTRA_PORTFOLIOS_CALLOUT',
  );
  const currentUser = useCurrentUser();
  const { track } = useAnalytics();
  const isMobile = useDeviceCategory() === 'mobile';
  const [addUserInterfaceFlag] = useAddUserInterfaceFlag();
  const { hasActivePortfolioSubscription } = useUpgradeToPro();

  const handleDismissCallout = useCallback(() => {
    if (!currentUser) {
      return;
    }

    track('button', {
      action: 'tapped',
      button_name: 'dismiss_explore_contra_portfolios',
    });
    addUserInterfaceFlag({
      onCompleted: () => {},
      onError: (error) => {
        captureException(error);
      },
      optimisticUpdater: (store) => {
        const userAccount = store.get(currentUser.id);
        userAccount?.setValue(
          [
            ...currentUser.userInterfaceFlags,
            'HIDE_EXPLORE_CONTRA_PORTFOLIOS_CALLOUT',
          ],
          'userInterfaceFlags',
        );
      },
      updater: (store) => {
        const userAccount = store.get(currentUser.id);
        userAccount?.setValue(
          [
            ...currentUser.userInterfaceFlags,
            'HIDE_EXPLORE_CONTRA_PORTFOLIOS_CALLOUT',
          ],
          'userInterfaceFlags',
        );
      },
      variables: {
        input: {
          userInterfaceFlag: 'HIDE_EXPLORE_CONTRA_PORTFOLIOS_CALLOUT',
        },
      },
    });
  }, [addUserInterfaceFlag, currentUser, track]);

  if (
    isHidden ||
    hasActivePortfolioSubscription ||
    currentUser?.selectedUserType !== 'CLIENT'
  ) {
    return null;
  }

  return (
    <Container>
      <CloseButton
        icon={CloseIcon}
        iconSize={isMobile ? 24 : 16}
        onClick={handleDismissCallout}
        size={isMobile ? 'small' : 'xsmall'}
      />
      <Column>
        <Text textStyle="sixthLevelHeadline">Explore Contra Portfolios</Text>
        <Text
          mb={isMobile ? 16 : 12}
          textStyle="bodyRegular"
        >
          A portfolio website, powered by your Contra profile.
        </Text>
        <Button
          as={Link}
          size={isMobile ? 'medium' : 'small'}
          target="_blank"
          to={getRoute('/portfolio')}
          variant="V2Secondary"
        >
          Check it out
        </Button>
      </Column>
      <Templates
        alt="Preview portfolio templates"
        src={templates}
      />
      <Shape
        alt="Quarter torus shape"
        src={shape}
      />
    </Container>
  );
};
